// Spec 45 component-behavior — cmp_ds_toast.
//
// Non-blocking transient feedback per spec 43 §feat_ds_pattern_toast_alert.
// Auto-dismiss timing per content-voice TOAST_AUTO_DISMISS_MS:
//   info / success → 5000 ms
//   warning        → 8000 ms
//   danger         → null (manual dismiss only — never auto-close)
//
// a11y: severity drives live-region urgency same as Alert.
//   info / success / warning → role='status' + aria-live='polite'
//   danger                   → role='alert' + aria-live='assertive'
//
// Lifecycle: caller renders Toast inside a portal/region the consumer
// controls. We don't ship a ToastProvider in 4b.4 — that's a Phase
// 4b.X composition (needs Modal/Portal primitives first). Toast as
// shipped here is the SURFACE; consumer wires the region.
//
// Pause-on-hover: spec 43 AC says "paused on hover / focus". The
// auto-dismiss timer pauses while the user has the toast hovered or
// focused, resumes when both leave. Implemented via mouse + focus
// event handlers + a remaining-ms ref so resume picks up where pause
// left off.

import { forwardRef, useCallback, useEffect, useRef, type HTMLAttributes, type MouseEvent, type ReactNode } from "react";

import { autoDismissMs, type ToastSeverity } from "@novemai/design-system";

import { cx } from "../lib/cx.ts";

export interface ToastProps extends HTMLAttributes<HTMLDivElement> {
  readonly severity: ToastSeverity;
  readonly title: string;
  readonly children?: ReactNode;
  /** Fires when the toast wants to dismiss (auto-timer expired OR close clicked). */
  readonly onDismiss: () => void;
  /** Override default per-severity dismiss timing. `null` = manual only. */
  readonly autoDismissMsOverride?: number | null;
  readonly action?: ReactNode;
  readonly dismissLabel?: string;
}

export const Toast = forwardRef<HTMLDivElement, ToastProps>(
  (
    {
      severity,
      title,
      action,
      onDismiss,
      autoDismissMsOverride,
      dismissLabel = "Dismiss notification",
      children,
      className,
      ...rest
    },
    ref,
  ) => {
    const isUrgent = severity === "danger";
    const totalMs = autoDismissMsOverride !== undefined ? autoDismissMsOverride : autoDismissMs(severity);

    const remainingMsRef = useRef(totalMs ?? null);
    const startedAtRef = useRef<number | null>(null);
    const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Latest-onDismiss ref pattern: callers usually pass an inline
    // arrow (`onDismiss={() => removeFromList(id)}`) which gets a new
    // identity every parent render. Capturing it in a ref + reading
    // from there in the timer callback means startTimer can stay
    // identity-stable, so the auto-dismiss useEffect doesn't reset
    // every time the parent re-renders. Without this, a ToastList
    // re-rendering on any sibling state change would silently reset
    // every toast's timer + nothing would ever auto-dismiss.
    const onDismissRef = useRef(onDismiss);
    useEffect(() => {
      onDismissRef.current = onDismiss;
    }, [onDismiss]);

    const clearTimer = useCallback(() => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    }, []);

    const startTimer = useCallback(
      (ms: number) => {
        clearTimer();
        startedAtRef.current = Date.now();
        timerRef.current = setTimeout(() => {
          onDismissRef.current();
        }, ms);
      },
      [clearTimer],
    );

    const pauseTimer = useCallback(() => {
      if (timerRef.current === null || startedAtRef.current === null || remainingMsRef.current === null) return;
      const elapsed = Date.now() - startedAtRef.current;
      remainingMsRef.current = Math.max(0, remainingMsRef.current - elapsed);
      clearTimer();
    }, [clearTimer]);

    const resumeTimer = useCallback(() => {
      if (remainingMsRef.current === null || timerRef.current !== null) return;
      if (remainingMsRef.current <= 0) {
        onDismissRef.current();
        return;
      }
      startTimer(remainingMsRef.current);
    }, [startTimer]);

    // Initial timer kick — null totalMs means manual-only (danger).
    // Re-runs ONLY when totalMs changes (severity flip / override change).
    // onDismiss is read via ref above so identity flips do NOT trip this.
    useEffect(() => {
      if (totalMs === null || totalMs <= 0) return;
      remainingMsRef.current = totalMs;
      startTimer(totalMs);
      return clearTimer;
    }, [totalMs, startTimer, clearTimer]);

    return (
      <div
        ref={ref}
        role={isUrgent ? "alert" : "status"}
        aria-live={isUrgent ? "assertive" : "polite"}
        data-component="toast"
        data-severity={severity}
        className={cx("nai-toast", className)}
        onMouseEnter={pauseTimer}
        onMouseLeave={resumeTimer}
        onFocus={pauseTimer}
        onBlur={resumeTimer}
        {...rest}
      >
        <div data-component="toast-title" className="nai-toast-title">
          {title}
        </div>
        {children !== undefined && (
          <div data-component="toast-body" className="nai-toast-body">
            {children}
          </div>
        )}
        {action !== undefined && (
          <div data-component="toast-action" className="nai-toast-action">
            {action}
          </div>
        )}
        <button
          type="button"
          data-component="toast-dismiss"
          aria-label={dismissLabel}
          className="nai-toast-dismiss"
          onClick={(event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            clearTimer();
            // Read via ref so the click handler always sees the latest
            // onDismiss (rare but possible: caller swaps onDismiss
            // between mousedown + mouseup).
            onDismissRef.current();
          }}
        >
          ×
        </button>
      </div>
    );
  },
);
Toast.displayName = "Toast";
